import { defineStore } from "pinia";
import type { DataValue, TimeFrame } from "@/types.ts";
import { StoreModule } from "@/constants/store.ts";
import api from "@/_helpers/api.ts";
import { axiosInstance } from "@/plugins/https.ts";
import {
  type AutoResolvedWidgetData,
  type CoroImpactSpotlightUser,
  getDefaultCoroImpactDateRange,
  type TicketsSpotlightWidgetData,
} from "@/_store/coro-impact/coro-impact.module.ts";
import { convertTimeFrameToUnix } from "@/_helpers/date";

export interface MspCoroImpactState {
  selectedDateRange: TimeFrame;
  topVulnerableUsers: DataValue<CoroImpactSpotlightUser[]>;
  topViolatorUsers: DataValue<CoroImpactSpotlightUser[]>;
  autoResolvedData: DataValue<AutoResolvedWidgetData>;
  ticketsSpotlightData: DataValue<TicketsSpotlightWidgetData>;
}

export const useMspCoroImpactStore = defineStore(StoreModule.MSP_CORO_IMPACT, {
  state: (): MspCoroImpactState => ({
    selectedDateRange: getDefaultCoroImpactDateRange(),
    topVulnerableUsers: { loading: false },
    topViolatorUsers: { loading: false },
    autoResolvedData: { loading: false },
    ticketsSpotlightData: { loading: false },
  }),

  getters: {
    unixTimeFrame: (state: MspCoroImpactState) => {
      return convertTimeFrameToUnix(state.selectedDateRange);
    },
  },

  actions: {
    async getTopVulnerableUsers() {
      try {
        this.topVulnerableUsers = { loading: true };
        const request = {
          ...api.mspCoroImpactVulnerableUsers(),
          params: { ...this.unixTimeFrame },
        };
        const { data } = await axiosInstance.request<CoroImpactSpotlightUser[]>(request);
        this.topVulnerableUsers.data = data;
      } catch (error) {
        this.topVulnerableUsers.error = true;
        console.error("Error fetching top vulnerable users:", error);
      } finally {
        this.topVulnerableUsers.loading = false;
      }
    },

    async getTopViolatorUsers() {
      try {
        this.topViolatorUsers = { loading: true };
        const request = {
          ...api.mspCoroImpactViolatorUsers(),
          params: { ...this.unixTimeFrame },
        };
        const { data } = await axiosInstance.request<CoroImpactSpotlightUser[]>(request);
        this.topViolatorUsers.data = data;
      } catch (error) {
        this.topViolatorUsers.error = true;
        console.error("Error fetching top violator users:", error);
      } finally {
        this.topViolatorUsers.loading = false;
      }
    },

    async getAutoResolvedWidgetData() {
      try {
        this.autoResolvedData = { loading: true };
        const request = {
          ...api.mspCoroImpactAutoResolvedTickets(),
          params: { ...this.unixTimeFrame },
        };
        const { data } = await axiosInstance.request<AutoResolvedWidgetData>(request);
        this.autoResolvedData.data = data;
      } catch (error) {
        this.autoResolvedData.error = true;
        console.error("Error fetching auto resolved tickets widget data:", error);
      } finally {
        this.autoResolvedData.loading = false;
      }
    },

    async getTicketsSpotlightWidgetData() {
      try {
        this.ticketsSpotlightData = { loading: true };
        const request = {
          ...api.mspCoroImpactTickets(),
          params: { ...this.unixTimeFrame },
        };
        const { data } = await axiosInstance.request<TicketsSpotlightWidgetData>(request);
        this.ticketsSpotlightData.data = data;
      } catch (error) {
        this.ticketsSpotlightData.error = true;
        console.error("Error fetching tickets spotlight widget data:", error);
      } finally {
        this.ticketsSpotlightData.loading = false;
      }
    },
  },
});
